import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { incidentDetailRoute } from '@/features/shared/routes';
import { createIncident } from '@ngsos/portal-core/commands';
import { resolveIncidentForCall } from '@ngsos/portal-core/incidents';
import type { Incident, PortalCall } from '@/signalr/contracts';
import { IncidentListMapComponent, useAgencyMapConfiguration } from '@/features/shared/map/IncidentListMapComponent';
import { PopupView } from '@/features/shared/map/PopupView';
import { CallNotificationsDetailTemplate } from './CallNotificationsDetailTemplate';
import { callNotificationMarkers } from './callNotificationMarkers';

export type CallNotificationsMapProps = {
  callNotifications: readonly PortalCall[];
  incidents: readonly Incident[];
  disableControls?: boolean;
};

export function CallNotificationsMap({
  callNotifications,
  incidents,
  disableControls = false,
}: CallNotificationsMapProps) {
  const navigate = useNavigate();
  const mapConfiguration = useAgencyMapConfiguration();

  const [center] = useState(() => mapConfiguration.defaultPosition);
  const [zoom] = useState(() => mapConfiguration.defaultZoom);

  const createIncidentInProgressRef = useRef(false);

  const [callNotification, setCallNotification] = useState<PortalCall | null>(null);

  const effectiveMarkers = useMemo(() => callNotificationMarkers(callNotifications), [callNotifications]);

  const openOrCreateIncident = async (item: PortalCall) => {
    const linkedIncident = resolveIncidentForCall(item, incidents);
    if (linkedIncident !== null) {
      void navigate(incidentDetailRoute(linkedIncident.id));
      return;
    }

    if (createIncidentInProgressRef.current) {
      return;
    }

    createIncidentInProgressRef.current = true;
    try {
      const requestedIncidentId = crypto.randomUUID();
      await createIncident(requestedIncidentId, [item.callId]);
      void navigate(incidentDetailRoute(requestedIncidentId));
    } finally {
      createIncidentInProgressRef.current = false;
    }
  };

  const onMarkerClicked = (markerId: string) => {
    setCallNotification(callNotifications.find((call) => call.id === markerId) ?? null);
  };

  return (
    <IncidentListMapComponent
      center={center}
      zoom={zoom}
      items={effectiveMarkers}
      disableControls={disableControls}
      onMarkerClicked={onMarkerClicked}
      popupContent={
        <PopupView show={callNotification != null} onClose={() => setCallNotification(null)}>
          {callNotification != null && (
            <CallNotificationsDetailTemplate
              item={callNotification}
              hasLinkedIncident={resolveIncidentForCall(callNotification, incidents) !== null}
              onOpenOrCreateIncident={openOrCreateIncident}
            />
          )}
        </PopupView>
      }
    />
  );
}
